// src/components/AccountRegister.js — QuickBooks-style registers for bank & credit card accounts
// [marker: registers v1]
// Date · Type · Number · Name · Account · Memo · Debit · Credit · Balance · ✓
// Write checks, card charges/credits, deposits, transfers, journal entries; reconcile.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { AccountSelect, useAccounts, TYPE_LABELS } from './AccountSelect';
import ReceivePaymentModal, { fmtDay, money, r2, todayStr } from './ReceivePaymentModal';
import { MakeDepositModal } from './PaymentsDeposits';
import { VendorModal } from './Vendors'; // [marker: vendors v1]
import { BillModal, BillPaymentModal } from './Bills'; // [marker: bills v1]
import { PrintChecksModal, printOneCheck } from './CheckPrint';
import { ClassSelect } from './ClassSelect'; // [marker: books-pro v1] classes on lines, void, customer credits
import { CreditMemoModal } from './CreditMemos';

const KIND_LABELS = {
  check: 'Write Check / Payment', bank_deposit: 'Other Deposit', charge: 'Card Charge', cc_credit: 'Card Credit / Refund',
  transfer: 'Transfer', journal: 'Journal Entry',
};
const actionsFor = (type) => (type === 'bank' ? ['check', 'bank_deposit', 'transfer', 'journal']
  : type === 'credit_card' ? ['charge', 'cc_credit', 'transfer', 'journal'] : ['journal', 'transfer']);

export default function AccountRegister({ canEdit, initialAccountId }) {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(initialAccountId || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ from: '', to: '' });
  const [entry, setEntry] = useState(null);     // { kind, id? }
  const [openDeposit, setOpenDeposit] = useState(null);
  const [openReceipt, setOpenReceipt] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [booksStart, setBooksStart] = useState(null);
  const [openBill, setOpenBill] = useState(null);       // [marker: bills v1]
  const [openBillPmt, setOpenBillPmt] = useState(null);
  const [printChecks, setPrintChecks] = useState(false);
  const [openCredit, setOpenCredit] = useState(null);

  const loadAccounts = useCallback(() => api.get('/bookkeeping/register-accounts').then(r => {
    setAccounts(r.data || []);
    setAccountId(cur => cur || (r.data || []).find(a => a.account_type === 'bank')?.id || (r.data || [])[0]?.id || null);
  }).catch(() => {}), []);
  useEffect(() => { loadAccounts(); api.get('/receivables/summary').then(r => setBooksStart(r.data.booksStart)).catch(() => {}); }, [loadAccounts]);
  useEffect(() => { if (initialAccountId) setAccountId(initialAccountId); }, [initialAccountId]);

  const load = useCallback(() => {
    if (!accountId) return;
    setLoading(true);
    const q = new URLSearchParams();
    if (range.from) q.set('from', range.from);
    if (range.to) q.set('to', range.to);
    api.get(`/bookkeeping/registers/${accountId}?${q}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Could not load the register.'))
      .finally(() => setLoading(false));
  }, [accountId, range]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); loadAccounts(); };

  const acct = data?.account;
  const isCard = acct?.account_type === 'credit_card';
  const credit = !!data?.creditNormal;
  // [marker: finish-up v1] plain words, like QuickBooks: Deposit / Payment (bank), Payment / Charge (card)
  const debitLabel = acct?.account_type === 'bank' ? 'Deposit' : isCard ? 'Payment' : credit ? 'Decrease' : 'Increase';
  const creditLabel = acct?.account_type === 'bank' ? 'Payment' : isCard ? 'Charge' : credit ? 'Increase' : 'Decrease';

  const toggleCleared = async (row) => {
    if (!canEdit || row.status === 'R') return;
    try {
      await api.patch(`/bookkeeping/lines/${row.line_id}/cleared`, { cleared: row.status !== 'C' });
      setData(d => ({ ...d, rows: d.rows.map(x => (x.line_id === row.line_id ? { ...x, status: row.status === 'C' ? '' : 'C' } : x)) }));
    } catch (err) { toast.error(err.response?.data?.error || 'Could not update.'); }
  };

  const openRow = (row) => {
    if (row.reference_type === 'deposit') return setOpenDeposit(row.reference_id);
    if (row.reference_type === 'receipt') return setOpenReceipt(row.reference_id);
    if (row.reference_type === 'bill') return setOpenBill(row.reference_id);
    if (row.reference_type === 'bill_payment') return setOpenBillPmt(row.reference_id);
    if (row.reference_type === 'credit_memo') return setOpenCredit(row.reference_id);
    if (row.editable_here) return setEntry({ id: row.entry_id, kind: row.kind || null });
    toast('This transaction comes from another screen (invoice or import) and can’t be edited here.', { icon: 'ℹ️' });
  };

  const columns = [
    { key: 'entry_date', label: 'Date', width: 100, type: 'date', get: r => (r.entry_date ? r.entry_date + 'T00:00:00' : null) },
    { key: 'type', label: 'Type', width: 85 },
    { key: 'number', label: 'Number', width: 95 },
    { key: 'name', label: 'Name', width: 190 },
    { key: 'other_account', label: 'Account', width: 190, render: r => <span title={r.other_accounts || ''}>{r.other_account || ''}</span> },
    { key: 'memo', label: 'Memo', width: 230 },
    { key: 'debit', label: debitLabel, width: 120, type: 'money', align: 'right', get: r => (parseFloat(r.debit) ? r.debit : null) },
    { key: 'credit', label: creditLabel, width: 120, type: 'money', align: 'right', get: r => (parseFloat(r.credit) ? r.credit : null) },
    { key: 'balance', label: credit ? 'Balance Owed' : 'Balance', width: 120, type: 'money', align: 'right' },
    { key: 'status', label: '✓', width: 48, text: r => (r.status === 'R' ? 'Reconciled' : r.status === 'C' ? 'Cleared' : ''),
      render: r => (
        <span onClick={e => { e.stopPropagation(); toggleCleared(r); }} title={r.status === 'R' ? 'Reconciled' : r.status === 'C' ? 'Cleared — click to untick' : 'Click to mark cleared'}
              style={{ cursor: r.status === 'R' || !canEdit ? 'default' : 'pointer', fontWeight: 700, color: r.status === 'R' ? 'var(--navy)' : 'var(--success)', display: 'inline-block', width: 20, textAlign: 'center' }}>
          {r.status === 'R' ? 'R' : r.status === 'C' ? '✓' : <span style={{ color: 'var(--gray-300)' }}>○</span>}
        </span>
      ) },
    { key: 'entry_number', label: 'Entry #', width: 110, defaultHidden: true },
    { key: 'created_by', label: 'Entered By', width: 130, defaultHidden: true, get: r => [r.created_by_first, r.created_by_last].filter(Boolean).join(' ') },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {accounts.filter(a => ['bank', 'credit_card'].includes(a.account_type)).map(a => (
          <button key={a.id} className={`btn btn-sm ${accountId === a.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setAccountId(a.id)}>
            {a.account_type === 'credit_card' ? '💳 ' : '🏦 '}{a.name}{a.account_last_four ? ` ••${a.account_last_four}` : ''}
            <span style={{ opacity: 0.75, marginLeft: 6 }}>{money(a.display_balance)}</span>
          </button>
        ))}
        <select className="form-select" style={{ width: 'auto', height: 31 }} value=""
                onChange={e => e.target.value && setAccountId(parseInt(e.target.value, 10))}>
          <option value="">Other account registers…</option>
          {accounts.filter(a => !['bank', 'credit_card'].includes(a.account_type)).map(a => (
            <option key={a.id} value={a.id}>{a.name} ({TYPE_LABELS[a.account_type]})</option>
          ))}
        </select>
      </div>

      {data && (
        <div className="stat-grid" style={{ marginBottom: 14 }}>
          <div className="stat-card"><div className="stat-label">{credit ? 'Balance Owed' : 'Current Balance'}</div><div className="stat-value">{money(data.currentBalance)}</div></div>
          <div className="stat-card"><div className="stat-label">Cleared Balance</div><div className="stat-value">{money(data.clearedBalance)}</div></div>
          <div className="stat-card"><div className="stat-label">Last Reconciled</div>
            <div className="stat-value" style={{ fontSize: '1rem' }}>{data.lastReconciliation ? `${fmtDay(data.lastReconciliation.statement_date)} · ${money(data.lastReconciliation.statement_ending_balance)}` : 'Never'}</div></div>
        </div>
      )}

      <DataGrid
        gridKey="account-register"
        rows={data?.rows || []}
        rowKey="line_id"
        loading={loading}
        columns={columns}
        onRowClick={openRow}
        totals={['debit', 'credit']}
        persistFilters={false}
        defaultSort={{ key: 'entry_date', dir: 'asc' }} /* [marker: fixes-oct21 v1] oldest at top, latest balance at the bottom */
        scrollToEnd
        height="calc(100vh - 420px)"
        emptyText="No transactions in this account yet."
        toolbarLeft={
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="date" className="form-input" style={{ height: 32, width: 150 }} value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} title="From" />
            <span style={{ color: 'var(--gray-400)' }}>–</span>
            <input type="date" className="form-input" style={{ height: 32, width: 150 }} value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} title="To" />
            {(range.from || range.to) && <button className="btn btn-ghost btn-sm" onClick={() => setRange({ from: '', to: '' })}>All dates</button>}
          </span>
        }
        toolbarRight={canEdit && acct && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            {actionsFor(acct.account_type).map(k => (
              <button key={k} className={`btn btn-sm ${k === actionsFor(acct.account_type)[0] ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setEntry({ kind: k })}>+ {KIND_LABELS[k]}</button>
            ))}
            {acct.account_type === 'bank' && (
              <button className="btn btn-secondary btn-sm" onClick={() => setPrintChecks(true)}>🖨 Print Checks</button>
            )}
            {['bank', 'credit_card'].includes(acct.account_type) && (
              <button className="btn btn-secondary btn-sm" onClick={() => setReconciling(true)}>✓ Reconcile</button>
            )}
          </span>
        )}
      />
      <p className="form-hint" style={{ marginTop: 8 }}>
        ✓ = cleared (click to tick), R = reconciled. Click a row to open it — deposits and customer payments open in their own windows.
      </p>

      {entry && acct && (
        <EntryModal account={acct} entryId={entry.id} kind={entry.kind} booksStart={booksStart}
                    onClose={() => setEntry(null)} onSaved={() => { setEntry(null); refresh(); }} />
      )}
      {openDeposit && (
        <MakeDepositModal depositId={openDeposit} canEdit={canEdit} onClose={() => setOpenDeposit(null)} onSaved={() => { setOpenDeposit(null); refresh(); }} />
      )}
      {openReceipt && (
        <ReceivePaymentModal receiptId={openReceipt} onClose={() => setOpenReceipt(null)} onSaved={() => { setOpenReceipt(null); refresh(); }} />
      )}
      {openBill && <BillModal billId={openBill} canEdit={canEdit} onClose={() => setOpenBill(null)} onSaved={() => { setOpenBill(null); refresh(); }}
                              onOpenPayment={(id) => { setOpenBill(null); setOpenBillPmt(id); }} />}
      {openBillPmt && <BillPaymentModal paymentId={openBillPmt} canEdit={canEdit} onClose={() => setOpenBillPmt(null)} onSaved={() => { setOpenBillPmt(null); refresh(); }} />}
      {openCredit && <CreditMemoModal memoId={openCredit} canEdit={canEdit} onClose={() => setOpenCredit(null)} onSaved={() => { setOpenCredit(null); refresh(); }} />}
      {printChecks && <PrintChecksModal canEdit={canEdit} onClose={() => setPrintChecks(false)} onDone={refresh} />}
      {reconciling && acct && (
        <ReconcileModal account={acct} canEdit={canEdit} onClose={() => { setReconciling(false); refresh(); }} />
      )}
    </div>
  );
}

// ─── Write check / charge / deposit / transfer / journal ─────────────────────
function EntryModal({ account, entryId, kind: kind0, booksStart, onClose, onSaved }) {
  const isEdit = !!entryId;
  const { accounts } = useAccounts();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState(null); // [marker: vendors v1]
  const [newVendor, setNewVendor] = useState(false);
  const [form, setForm] = useState({ kind: kind0 || 'check', date: todayStr(), number: '', payee: '', memo: '', toAccountId: null, amount: '', toPrint: false, payeeAddress: '' }); // [marker: bills v1] toPrint, payeeAddress
  const [printed, setPrinted] = useState(null);
  const [voided, setVoided] = useState(null);
  const [lines, setLines] = useState([{ accountId: null, memo: '', amount: '' }]);
  const [jlines, setJlines] = useState([{ accountId: account.id, memo: '', debit: '', credit: '' }, { accountId: null, memo: '', debit: '', credit: '' }]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/vendors?active=all').then(r => setVendors(r.data || [])).catch(() => {});
    if (!isEdit) return;
    api.get(`/bookkeeping/entries/${entryId}`).then(r => {
      const je = r.data;
      setLocked(!!je.reconciled || !!je.voided_at);
      setVoided(je.voided_at ? { at: je.voided_at, reason: je.void_reason } : null);
      setVendorId(je.vendor_id || null);
      const home = je.lines.find(l => l.account_id === account.id) || je.lines[0];
      const others = je.lines.filter(l => l !== home);
      let kind = je.kind;
      if (!kind) {
        // older entries: guess from the side the register account is on
        if (others.length === 0) kind = account.account_type === 'credit_card'
          ? (parseFloat(home.credit) > 0 ? 'charge' : 'cc_credit') : (parseFloat(home.credit) > 0 ? 'check' : 'bank_deposit');
        else if (others.length === 1 && ['bank', 'credit_card'].includes(others[0].account_type) && ['bank', 'credit_card'].includes(account.account_type)) kind = 'transfer';
        else kind = account.account_type === 'credit_card'
          ? (parseFloat(home.credit) > 0 ? 'charge' : 'cc_credit') : (parseFloat(home.credit) > 0 ? 'check' : 'bank_deposit');
      }
      setForm({
        kind, date: je.entry_date, number: je.ref_number || home.check_number || '', payee: je.payee || home.payee || '',
        memo: je.description || '', toAccountId: kind === 'transfer' ? (others[0]?.account_id || null) : null,
        amount: String(r2(parseFloat(home.debit) + parseFloat(home.credit))),
        toPrint: !!je.to_print, payeeAddress: je.payee_address || '',
      });
      setPrinted(je.printed_at || null);
      if (kind === 'journal') {
        setJlines(je.lines.map(l => ({ accountId: l.account_id, classId: l.class_id || null, memo: l.description || '', debit: parseFloat(l.debit) ? String(r2(l.debit)) : '', credit: parseFloat(l.credit) ? String(r2(l.credit)) : '' })));
      } else if (kind !== 'transfer') {
        setLines(others.length
          ? others.map(l => ({ accountId: l.account_id, classId: l.class_id || null, memo: l.description === je.description ? '' : (l.description || ''), amount: String(r2(parseFloat(l.debit) + parseFloat(l.credit))) }))
          : [{ accountId: null, memo: '', amount: String(r2(parseFloat(home.debit) + parseFloat(home.credit))) }]);
      }
    }).catch(() => toast.error('Could not load the transaction.')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = r2(lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
  const jd = r2(jlines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0));
  const jc = r2(jlines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0));
  const kind = form.kind;
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const setJ = (i, k, v) => setJlines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  // [marker: vendors v1] typing or picking a name links the vendor and fills its usual account
  const pickVendor = (v) => {
    setVendorId(v ? v.id : null);
    if (v && !form.payeeAddress) {
      const cityLine = [v.city, [v.state, v.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      const addr = [v.address_line1, v.address_line2, cityLine].filter(Boolean).join('\n');
      if (addr) set('payeeAddress', addr);
    }
    if (v && v.default_account_id && kind !== 'journal' && kind !== 'transfer') {
      setLines(ls => (ls.length === 1 && !ls[0].accountId ? [{ ...ls[0], accountId: v.default_account_id }] : ls));
    }
  };
  const onPayee = (text) => {
    set('payee', text);
    const v = vendors.find(x => x.name.trim().toLowerCase() === text.trim().toLowerCase() && (x.is_active || x.id === vendorId));
    pickVendor(v || null);
  };

  const save = async () => {
    const body = { kind, accountId: account.id, date: form.date, number: form.number, payee: form.payee, memo: form.memo, vendorId: vendorId || null,
                   toPrint: kind === 'check' && form.toPrint, payeeAddress: kind === 'check' ? form.payeeAddress : null };
    if (kind === 'transfer') Object.assign(body, { toAccountId: form.toAccountId, amount: r2(form.amount) });
    else if (kind === 'journal') body.lines = jlines.map(l => ({ accountId: l.accountId, classId: l.classId || null, memo: l.memo, debit: r2(l.debit), credit: r2(l.credit) }));
    else body.lines = lines.map(l => ({ accountId: l.accountId, classId: l.classId || null, memo: l.memo, amount: r2(l.amount) }));
    setSaving(true);
    try {
      const r = isEdit ? await api.patch(`/bookkeeping/entries/${entryId}`, body) : await api.post('/bookkeeping/entries', body);
      if (r.data?.notice) toast(r.data.notice, { icon: 'ℹ️', duration: 6000 }); else toast.success(body.toPrint ? 'Saved — the check is waiting under 🖨 Print Checks.' : isEdit ? 'Saved.' : 'Recorded.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };
  const voidIt = async () => {
    const reason = window.prompt('Void this transaction? It stays in the register at $0, marked VOID, with its number.\n\nReason (optional):', '');
    if (reason === null) return;
    setSaving(true);
    try { const r = await api.post(`/bookkeeping/entries/${entryId}/void`, { reason }); toast.success(r.data.message || 'Voided.'); onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not void.'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this transaction? This cannot be undone.')) return;
    setSaving(true);
    try { await api.delete(`/bookkeeping/entries/${entryId}`); toast.success('Deleted.'); onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
    finally { setSaving(false); }
  };

  const otherAccounts = accounts.filter(a => a.id !== account.id);
  const transferTargets = accounts.filter(a => a.id !== account.id && ['bank', 'credit_card', 'asset', 'other_liability', 'loan'].includes(a.account_type));
  const payeeLabel = kind === 'bank_deposit' || kind === 'cc_credit' ? 'Received From' : 'Pay To / Name';

  return (
    <>
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1400 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 860 }}>
        <div className="modal-header">
          <span className="modal-title">{KIND_LABELS[kind] || 'Transaction'} — {account.name}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading-page" style={{ minHeight: 160 }}><div className="spinner" /></div> : (
            <>
              {voided ? <div style={{ fontSize: '0.85rem', background: 'var(--gray-100)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                <strong>VOID</strong>{voided.reason ? ` — ${voided.reason}` : ''}. It stays in the register at $0 with its number.</div>
              : locked && <div style={{ fontSize: '0.82rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
                This transaction is reconciled. You can fix the name, number and memo; amounts and accounts are locked.</div>}
              {isEdit && (
                <div className="form-group" style={{ maxWidth: 260 }}>
                  <label className="form-label">Type</label>
                  <select className="form-select" value={kind} disabled={locked} onChange={e => set('kind', e.target.value)}>
                    {actionsFor(account.account_type).map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={form.date} disabled={locked} onChange={e => set('date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">{kind === 'check' ? 'Check #' : 'Number / Ref'}</label>
                  {kind === 'check' && form.toPrint
                    ? <input className="form-input" value="To print" disabled />
                    : <input className="form-input" value={form.number} onChange={e => set('number', e.target.value)} placeholder={kind === 'check' ? 'e.g. 1045 or EFT' : ''} />}
                  {kind === 'check' && !locked && (
                    <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: '0.78rem', marginTop: 4 }}>
                      <input type="checkbox" checked={form.toPrint} onChange={e => set('toPrint', e.target.checked)} /> Print this check
                    </label>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">{payeeLabel}</label>
                  <input className="form-input" list="register-payees" value={form.payee} onChange={e => onPayee(e.target.value)}
                         placeholder={kind === 'check' || kind === 'charge' ? 'Start typing a vendor…' : ''} />
                  <datalist id="register-payees">{vendors.filter(v => v.is_active || v.id === vendorId).map(v => <option key={v.id} value={v.name} />)}</datalist>
                  {/* [marker: vendors v1] */}
                  {vendorId ? (
                    <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: 3 }}>
                      ✓ Linked to vendor {vendors.find(v => v.id === vendorId)?.name || ''}
                      <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '0 6px', fontSize: '0.72rem' }} onClick={() => setVendorId(null)}>unlink</button>
                    </div>
                  ) : form.payee.trim() && kind !== 'transfer' ? (
                    <div style={{ fontSize: '0.75rem', marginTop: 3 }}>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 0, fontSize: '0.75rem' }} onClick={() => setNewVendor(true)}>+ Save “{form.payee.trim()}” as a vendor</button>
                    </div>
                  ) : null}
                </div>
              </div>
              {booksStart && form.date && form.date < booksStart && (
                <p className="form-hint" style={{ color: '#b45309', marginTop: -6 }}>
                  This date is before {fmtDay(booksStart)}. QuickBooks history up to then will be imported, so only enter it here if it isn’t in QuickBooks.
                </p>
              )}

              {kind === 'transfer' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Transfer to *</label>
                    <AccountSelect accounts={transferTargets} value={form.toAccountId} disabled={locked} onChange={v => set('toAccountId', v)} placeholder="— Account (e.g. the credit card being paid) —" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount *</label>
                    <input className="form-input" type="number" step="0.01" value={form.amount} disabled={locked} onChange={e => set('amount', e.target.value)} />
                  </div>
                </div>
              ) : kind === 'journal' ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.3fr 1.6fr 1fr 1fr auto', gap: 6, fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: 4 }}>
                    <span>Account</span><span>Class</span><span>Memo</span><span style={{ textAlign: 'right' }}>Debit</span><span style={{ textAlign: 'right' }}>Credit</span><span />
                  </div>
                  {jlines.map((l, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.3fr 1.6fr 1fr 1fr auto', gap: 6, marginBottom: 6 }}>
                      <AccountSelect accounts={accounts} value={l.accountId} disabled={locked} onChange={v => setJ(i, 'accountId', v)} />
                      <ClassSelect value={l.classId} disabled={locked} onChange={v => setJ(i, 'classId', v)} />
                      <input className="form-input" value={l.memo} onChange={e => setJ(i, 'memo', e.target.value)} />
                      <input className="form-input" type="number" step="0.01" style={{ textAlign: 'right' }} value={l.debit} disabled={locked} onChange={e => setJ(i, 'debit', e.target.value)} />
                      <input className="form-input" type="number" step="0.01" style={{ textAlign: 'right' }} value={l.credit} disabled={locked} onChange={e => setJ(i, 'credit', e.target.value)} />
                      <button className="btn btn-ghost btn-sm" disabled={locked} onClick={() => setJlines(ls => ls.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button className="btn btn-secondary btn-sm" disabled={locked} onClick={() => setJlines(ls => [...ls, { accountId: null, memo: '', debit: '', credit: '' }])}>+ Add line</button>
                    <span style={{ fontSize: '0.85rem', color: Math.abs(jd - jc) > 0.005 ? 'var(--danger)' : 'var(--success)' }}>Debits {money(jd)} · Credits {money(jc)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: 4 }}>
                    {kind === 'check' || kind === 'charge' ? 'What it was for (expense accounts)' : 'Where the money came from'}
                  </div>
                  {lines.map((l, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.4fr 1.6fr 1fr auto', gap: 6, marginBottom: 6 }}>
                      <AccountSelect accounts={otherAccounts} value={l.accountId} disabled={locked} onChange={v => setLine(i, 'accountId', v)}
                                     types={kind === 'check' || kind === 'charge' ? undefined : undefined} placeholder="— Account —" />
                      <ClassSelect value={l.classId} disabled={locked} onChange={v => setLine(i, 'classId', v)} placeholder="— Class (event / program) —" />
                      <input className="form-input" placeholder="Memo" value={l.memo} onChange={e => setLine(i, 'memo', e.target.value)} />
                      <input className="form-input" type="number" step="0.01" placeholder="Amount" style={{ textAlign: 'right' }} value={l.amount} disabled={locked} onChange={e => setLine(i, 'amount', e.target.value)} />
                      <button className="btn btn-ghost btn-sm" disabled={locked || lines.length === 1} onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button className="btn btn-secondary btn-sm" disabled={locked} onClick={() => setLines(ls => [...ls, { accountId: null, memo: '', amount: '' }])}>+ Split</button>
                    <span>Total: <strong>{money(total)}</strong></span>
                  </div>
                </>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: kind === 'check' ? '1fr 1fr' : '1fr', gap: 12, marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Memo{kind === 'check' ? ' (prints on the check)' : ''}</label>
                  <input className="form-input" value={form.memo} onChange={e => set('memo', e.target.value)} />
                </div>
                {kind === 'check' && (
                  <div className="form-group">
                    <label className="form-label">Address (prints on the check, for a window envelope)</label>
                    <textarea className="form-textarea" rows={3} value={form.payeeAddress} onChange={e => set('payeeAddress', e.target.value)} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isEdit && !locked ? (
            <span style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-danger" onClick={remove} disabled={saving}>Delete</button>
              <button className="btn btn-secondary" onClick={voidIt} disabled={saving} title="Keep it in the register at $0 (QuickBooks-style), e.g. a check that was printed but not used">Void</button>
            </span>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            {isEdit && kind === 'check' && !form.toPrint && printed && (
              <button className="btn btn-secondary" onClick={() => printOneCheck('entry', entryId).catch(e => toast.error(e.message))}>🖨 Reprint</button>
            )}
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            {!voided && <button className="btn btn-primary" onClick={save} disabled={saving || loading}>{saving ? 'Saving…' : 'Save'}</button>}
          </div>
        </div>
      </div>
    </div>
    {newVendor && (
      <VendorModal initialName={form.payee.trim()} onClose={() => setNewVendor(false)}
        onSaved={(v) => { setNewVendor(false); if (v) { setVendors(vs => [...vs, v]); set('payee', v.name); pickVendor(v); } }} />
    )}
    </>
  );
}

// ─── Reconcile ────────────────────────────────────────────────────────────────
function ReconcileModal({ account, canEdit, onClose }) {
  const [step, setStep] = useState('start');
  const [statementDate, setStatementDate] = useState(todayStr());
  const [ending, setEnding] = useState('');
  const [data, setData] = useState(null);
  const [ticked, setTicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (date) => {
    const r = (await api.get(`/bookkeeping/reconcile/${account.id}${date ? `?statementDate=${date}` : ''}`)).data;
    setData(r);
    setTicked(new Set(r.items.filter(i => i.is_cleared).map(i => i.line_id)));
    return r;
  }, [account.id]);
  useEffect(() => {
    load().then(r => {
      if (r.draft) { setStatementDate(r.draft.statement_date); setEnding(String(r2(r.draft.statement_ending_balance))); }
    }).catch(() => toast.error('Could not load.'));
  }, [load]);

  const sign = data?.creditNormal ? -1 : 1;
  const items = data?.items || [];
  const tickedAmt = r2(items.filter(i => ticked.has(i.line_id)).reduce((s, i) => s + sign * (r2(i.debit) - r2(i.credit)), 0));
  const cleared = r2((data?.beginningBalance || 0) + tickedAmt);
  const diff = r2(r2(ending) - cleared);
  const toggle = (id) => setTicked(t => { const n = new Set(t); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const inLabel = account.account_type === 'credit_card' ? 'Payments & Credits' : 'Deposits & Other Credits';
  const outLabel = account.account_type === 'credit_card' ? 'Charges & Cash Advances' : 'Checks & Payments';
  const ins = items.filter(i => r2(i.debit) > 0);
  const outs = items.filter(i => r2(i.credit) > 0);

  const submit = async (finish) => {
    setBusy(true);
    try {
      const r = await api.post(`/bookkeeping/reconcile/${account.id}`, { statementDate, endingBalance: r2(ending), clearedLineIds: Array.from(ticked), finish });
      if (finish) { toast.success(`Reconciled — ${r.data.items} item(s).`); onClose(); }
      else { toast.success('Saved. Come back any time to finish.'); onClose(); }
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const undo = async () => {
    if (!window.confirm('Undo the last reconciliation? Its items stay ticked but are no longer locked.')) return;
    setBusy(true);
    try { const r = await api.post(`/bookkeeping/reconcile/${account.id}/undo`); toast.success(r.data.message); await load(statementDate); setStep('start'); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not undo.'); }
    finally { setBusy(false); }
  };

  const List = ({ title, rows, field }) => (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden', minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--gray-50)', fontSize: '0.8rem', fontWeight: 700 }}>
        <span>{title}</span>
        <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', height: 22 }}
                onClick={() => setTicked(t => { const n = new Set(t); const all = rows.every(r => n.has(r.line_id)); rows.forEach(r => (all ? n.delete(r.line_id) : n.add(r.line_id))); return n; })}>
          Mark all
        </button>
      </div>
      <div style={{ maxHeight: 330, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <tbody>
            {rows.length === 0 && <tr><td style={{ padding: 10, color: 'var(--gray-500)' }}>None</td></tr>}
            {rows.map(r => (
              <tr key={r.line_id} onClick={() => toggle(r.line_id)} style={{ cursor: 'pointer', borderTop: '1px solid var(--gray-100)', background: ticked.has(r.line_id) ? '#eff6ff' : undefined }}>
                <td style={{ padding: '4px 6px', width: 22 }}><input type="checkbox" readOnly checked={ticked.has(r.line_id)} /></td>
                <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{fmtDay(r.entry_date)}</td>
                <td style={{ padding: '4px 6px' }}>{r.number}</td>
                <td style={{ padding: '4px 6px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.memo}>{r.name || r.memo}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{money(r[field])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1400 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 1060 }}>
        <div className="modal-header">
          <span className="modal-title">Reconcile — {account.name}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!data ? <div className="loading-page" style={{ minHeight: 160 }}><div className="spinner" /></div> : step === 'start' ? (
            <>
              <p style={{ marginTop: 0, fontSize: '0.875rem' }}>Enter the date and ending balance from the {account.account_type === 'credit_card' ? 'card' : 'bank'} statement.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, maxWidth: 640 }}>
                <div className="form-group"><label className="form-label">Statement Date</label>
                  <input className="form-input" type="date" value={statementDate} onChange={e => setStatementDate(e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Beginning Balance</label>
                  <input className="form-input" value={money(data.beginningBalance)} disabled /></div>
                <div className="form-group"><label className="form-label">Ending Balance *</label>
                  <input className="form-input" type="number" step="0.01" value={ending} onChange={e => setEnding(e.target.value)} placeholder="From the statement" /></div>
              </div>
              {data.history.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: 4 }}>Past reconciliations</div>
                  <table style={{ fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                    <tbody>
                      {data.history.map((h, i) => (
                        <tr key={h.id}>
                          <td style={{ padding: '2px 10px 2px 0' }}>{fmtDay(h.statement_date)}</td>
                          <td style={{ padding: '2px 10px' }}>Ending {money(h.statement_ending_balance)}</td>
                          <td style={{ padding: '2px 10px', color: 'var(--gray-500)' }}>{h.items} items · {[h.first_name, h.last_name].filter(Boolean).join(' ')}</td>
                          <td>{i === 0 && canEdit && <button className="btn btn-ghost btn-sm" onClick={undo} disabled={busy}>Undo</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <List title={inLabel} rows={ins} field="debit" />
                <List title={outLabel} rows={outs} field="credit" />
              </div>
              <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end', marginTop: 12, fontSize: '0.9rem', flexWrap: 'wrap' }}>
                <span>Beginning {money(data.beginningBalance)}</span>
                <span>Cleared Balance <strong>{money(cleared)}</strong></span>
                <span>Ending Balance <strong>{money(ending)}</strong></span>
                <span style={{ color: Math.abs(diff) < 0.005 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>Difference {money(diff)}</span>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          {step === 'start' ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={!data || ending === '' || !statementDate}
                      onClick={async () => { await load(statementDate); setStep('items'); }}>Continue</button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => setStep('start')}>← Back</button>
              <button className="btn btn-secondary" onClick={() => submit(false)} disabled={busy || !canEdit}>Save for Later</button>
              <button className="btn btn-primary" onClick={() => submit(true)} disabled={busy || !canEdit || Math.abs(diff) > 0.005}>Reconcile Now</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
